import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import DocumentChatShell from '../shells/DocumentChatShell';
import AssistantBotRow from '../shells/AssistantBotRow';
import { useDocumentChatNavigation } from '../shells/useDocumentChatNavigation';
import ChatSaveErrorBanner from '../components/ChatSaveErrorBanner';
import ContinueButtonBar from '../components/ContinueButtonBar';
import DocumentChatLayout from '../layout/DocumentChatLayout';
import { useChatThreadScroll } from '../hooks/useChatThreadScroll';
import CareerUpConfirmationChatComposer, {
  CareerUpConfirmationDeclinedComposer,
} from './CareerUpConfirmationChatComposer';
import CareerUpConfirmationChatThread from './CareerUpConfirmationChatThread';
import { useCareerUpConfirmationFlow } from './useCareerUpConfirmationFlow';

const FIRST_ASSISTANT_DELAY_MS = 1000;
const FOLLOW_ASSISTANT_GAP_MS = 650;

export interface CareerUpConfirmationChatFlowProps {
  documentTitle: string;
  currentEmail?: string | null;
  invitationId?: string | null;
  onBack: () => void;
  onCompleteContinue?: () => void | Promise<void>;
  onNoCardContinue?: () => void | Promise<void>;
  onUploadNeededContinue?: () => void | Promise<void>;
}

const CareerUpConfirmationChatFlow: React.FC<CareerUpConfirmationChatFlowProps> = ({
  currentEmail,
  invitationId,
  onBack,
  onCompleteContinue,
  onNoCardContinue,
  onUploadNeededContinue,
}) => {
  const { t } = useLanguage();
  const { exitToChecklist } = useDocumentChatNavigation(onBack);
  const flow = useCareerUpConfirmationFlow(currentEmail, invitationId, t);

  const [revealPhase, setRevealPhase] = useState(0);
  const threadEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (flow.isLoading) return;
    setRevealPhase(0);
    const t1 = window.setTimeout(() => setRevealPhase(1), FIRST_ASSISTANT_DELAY_MS);
    const t2 = window.setTimeout(() => setRevealPhase(2), FIRST_ASSISTANT_DELAY_MS + FOLLOW_ASSISTANT_GAP_MS);
    const t3 = window.setTimeout(
      () => setRevealPhase(3),
      FIRST_ASSISTANT_DELAY_MS + FOLLOW_ASSISTANT_GAP_MS * 2
    );
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
    };
  }, [flow.isLoading]);

  useChatThreadScroll(threadEndRef, [
    revealPhase,
    flow.committed.length,
    flow.isComplete,
    flow.noCardDeclined,
    flow.careerUpCardUploadNeeded,
    flow.driversLicenseUploadNeeded,
    flow.assistantQuestionVisible,
    flow.editingCommitIndex,
  ]);

  const canShowThread = revealPhase >= 3 && !flow.isLoading;
  const uploadNeeded =
    (flow.careerUpCardUploadNeeded || flow.driversLicenseUploadNeeded) && onUploadNeededContinue;

  return (
    <DocumentChatShell onBack={exitToChecklist} cancelLabel={t('submitDocuments.chat.cancelProcedure')}>
      <DocumentChatLayout
        thread={
          <>
            {revealPhase >= 1 && (
              <AssistantBotRow animateIn>{t('submitDocuments.careerUpConfirmation.intro1')}</AssistantBotRow>
            )}
            {revealPhase >= 2 && (
              <AssistantBotRow animateIn>{t('submitDocuments.careerUpConfirmation.intro2')}</AssistantBotRow>
            )}
            {flow.isLoading ? (
              revealPhase >= 2 && (
                <AssistantBotRow animateIn>{t('submitDocuments.careerUpConfirmation.loading')}</AssistantBotRow>
              )
            ) : canShowThread ? (
              <CareerUpConfirmationChatThread
                flow={flow}
                currentEmail={currentEmail}
                invitationId={invitationId}
              />
            ) : null}
            {flow.isComplete && (
              <AssistantBotRow animateIn>{t('submitDocuments.careerUpConfirmation.completeThankYou')}</AssistantBotRow>
            )}
            <div ref={threadEndRef} aria-hidden />
            <div ref={flow.threadEndRef} aria-hidden />
          </>
        }
        composer={
          <>
            <ChatSaveErrorBanner message={flow.saveError} />
            {flow.noCardDeclined && onNoCardContinue && canShowThread && (
              <CareerUpConfirmationDeclinedComposer onContinue={() => void onNoCardContinue()} />
            )}
            {uploadNeeded && canShowThread && (
              <CareerUpConfirmationDeclinedComposer onContinue={() => void onUploadNeededContinue()} />
            )}
            {flow.isComplete && onCompleteContinue && (
              <ContinueButtonBar
                onContinue={() => {
                  void (async () => {
                    const ok = await flow.handleFinalComplete();
                    if (ok) onCompleteContinue();
                  })();
                }}
              />
            )}
            {!flow.isComplete &&
              !flow.noCardDeclined &&
              !flow.careerUpCardUploadNeeded &&
              !flow.driversLicenseUploadNeeded &&
              !flow.isLoading &&
              canShowThread && <CareerUpConfirmationChatComposer flow={flow} />}
          </>
        }
      />
    </DocumentChatShell>
  );
};

export default CareerUpConfirmationChatFlow;
